/**
 * Building and interpreting commands for the sensor command channel
 * (`fff00200`).
 *
 * Wire format (confirmed on hardware 2026-08-25): `:<deviceId>:<code>:<value>\r\n`
 * — leading colon, colon-delimited, CRLF terminator. Writes take effect live
 * (no reset needed), though the settings characteristic refreshes a beat after
 * the acknowledgement.
 */
import type {
	SensorCommandResultModel,
	SensorEchoClassification,
} from './types';
import { SensorCommandCode } from './types';

/**
 * Terminator for EVERY command — a bare LF.
 *
 * CORRECTED 2026-08-26 from the vendor binary: BX_BLE.exe's command builder
 * routes all cases (settings AND actions) through `0x140006692`, which
 * appends the `"\n"` constant at `0x14002aa18`. It never sends CRLF.
 *
 * The earlier CRLF conclusion came from settings-write experiments where the
 * extra CR was invisible: numeric fields (`upp`, `LD_n`) parse with an
 * integer scan that stops at the CR, so those writes looked perfect. STRING
 * fields (`url`, `api_key`, `apn_name`) store the value verbatim, so a CRLF
 * terminator leaves a trailing CR INSIDE the stored setting — a URL that
 * cannot connect and a key that cannot authenticate, neither visible when
 * the block is read back and rendered.
 */
export const SENSOR_COMMAND_TERMINATOR = '\n';

/**
 * Kept as a distinct name for call-site clarity; identical to
 * {@link SENSOR_COMMAND_TERMINATOR} now that both kinds are known to use a
 * bare LF. Actions surfaced the bug first: a keyword is matched whole, so
 * `enaddrmode\r` was rejected outright (`fail=-3` on unit 7AD2E7EC).
 */
export const SENSOR_ACTION_TERMINATOR = '\n';

/** Whether a command carries a free value or a fixed action keyword. */
export type SensorCommandKind = 'value' | 'action';

/** Static metadata for each command, used by the UI and for action keywords. */
export interface SensorCommandDef {
	code: SensorCommandCode;
	/** Stable machine key. */
	key: string;
	/** Human label (untranslated; wrap with `t()` at the render site). */
	label: string;
	kind: SensorCommandKind;
	/** For actions: the keyword sent in the value slot. */
	keyword?: string;
	/**
	 * Re-maps or wipes module addressing / reboots — never send to a live unit
	 * without an explicit re-commission.
	 */
	destructive?: boolean;
}

export const SENSOR_COMMANDS: ReadonlyArray<SensorCommandDef> = [
	{
		code: SensorCommandCode.ReportInterval,
		key: 'upp',
		label: 'Report interval',
		kind: 'value',
	},
	{
		code: SensorCommandCode.ModuleCount,
		key: 'moduleCount',
		label: 'Module count',
		kind: 'value',
	},
	{
		code: SensorCommandCode.ServerUrl,
		key: 'serverUrl',
		label: 'Server URL',
		kind: 'value',
	},
	{
		code: SensorCommandCode.ApiKey,
		key: 'apiKey',
		label: 'API key',
		kind: 'value',
	},
	{
		code: SensorCommandCode.ApnName,
		key: 'apnName',
		label: 'APN name',
		kind: 'value',
	},
	{
		code: SensorCommandCode.Reset,
		key: 'reset',
		label: 'Reset (reboot)',
		kind: 'action',
		keyword: 'reset',
		destructive: true,
	},
	{
		code: SensorCommandCode.EnableAddressing,
		key: 'enableAddressing',
		label: 'Start module addressing',
		kind: 'action',
		keyword: 'enaddrmode',
		destructive: true,
	},
	{
		code: SensorCommandCode.ClearAddressing,
		key: 'clearAddressing',
		label: 'Clear module addressing',
		kind: 'action',
		keyword: 'all_addr_clr',
		destructive: true,
	},
];

const COMMANDS_BY_CODE: ReadonlyMap<string, SensorCommandDef> = new Map(
	SENSOR_COMMANDS.map((c) => [c.code, c]),
);

/** Look up a command definition by its code. */
export function getSensorCommandDef(
	code: SensorCommandCode,
): SensorCommandDef | undefined {
	return COMMANDS_BY_CODE.get(code);
}

/**
 * Build the wire string for a command: `:<deviceId>:<code>:<value>\r\n`.
 *
 * `value` for a settings command is the new setting; for an action command it is
 * the action keyword (see {@link buildSensorAction}). Throws on an empty device
 * id, because an id-less command was not accepted by the firmware.
 */
export function buildSensorCommand(
	deviceId: string,
	code: SensorCommandCode,
	value: string | number,
	terminator: string = SENSOR_COMMAND_TERMINATOR,
): string {
	if (deviceId.trim() === '') {
		throw new Error('buildSensorCommand: deviceId is required');
	}
	return `:${deviceId}:${code}:${String(value)}${terminator}`;
}

/**
 * Build an action command (reset / enaddrmode / all_addr_clr) using its keyword.
 * Throws if the code is not an action command.
 */
export function buildSensorAction(
	deviceId: string,
	code: SensorCommandCode,
): string {
	const def = getSensorCommandDef(code);
	if (def == null || def.kind !== 'action' || def.keyword == null) {
		throw new Error(`buildSensorAction: ${code} is not an action command`);
	}
	// Actions take the vendor's bare-LF terminator (see
	// SENSOR_ACTION_TERMINATOR) — a trailing CR makes the firmware reject the
	// keyword.
	return buildSensorCommand(
		deviceId,
		code,
		def.keyword,
		SENSOR_ACTION_TERMINATOR,
	);
}

/** Encode a built command string to bytes for the BLE write. */
export function encodeSensorCommand(command: string): Uint8Array {
	return new TextEncoder().encode(command);
}

/** Strip trailing NULs / CR / LF and surrounding whitespace from a read value. */
function cleanEcho(echo: string): string {
	return echo
		.replace(/\0+$/, '')
		.replace(/[\r\n]+$/, '')
		.trim();
}

/**
 * Parse a device command result of the form `:<deviceId>:<code>:<result>`.
 * Returns `null` when the text is not a device result (e.g. it's our own
 * written bytes read back, or an empty buffer).
 */
export function parseSensorResult(
	echo: string | null | undefined,
): SensorCommandResultModel | null {
	if (echo == null) {
		return null;
	}
	const clean = cleanEcho(echo);
	// The channel buffer keeps stale bytes from longer previous messages, so
	// a fresh (shorter) device result can be followed by a newline and tail
	// junk — e.g. ":id:9:fail=-3\ne" observed on hardware, where the trailing
	// "e" is debris from the earlier ":id:9:enaddrmode" write. Match against
	// the FIRST LINE only; a $-anchored match across the whole buffer made a
	// genuine device reply read as no-result.
	const firstLine = clean.split(/[\r\n]/, 1)[0] ?? '';
	const match = /^:([^:]+):([^:]+):(.*)$/.exec(firstLine);
	if (match == null) {
		return null;
	}
	return {
		deviceId: match[1] ?? '',
		code: match[2] ?? '',
		// NUL debris can still trail inside the first line.
		result: (match[3] ?? '').replace(/\0.*$/, ''),
		raw: clean,
	};
}

/**
 * Classify what a `fff00200` read shows after writing a command, distinguishing
 * a real device result from our own bytes being read back out of the buffer.
 *
 * `sentBody` is the command string WITHOUT its terminator (what we wrote, minus
 * `\r\n`). `value` is the value/keyword we sent, so a result that merely echoes
 * our value is not mistaken for a device acknowledgement.
 */
export function classifySensorEcho(
	echo: string,
	deviceId: string,
	code: SensorCommandCode,
	value: string,
	sentBody: string,
): SensorEchoClassification {
	const clean = cleanEcho(echo);
	const rawReadback = clean.startsWith(cleanEcho(sentBody));
	const parsedResult = parseSensorResult(clean);
	const commandCode: string = code;
	// The firmware does not reliably echo the command code back: a settings
	// write can be acknowledged under code `0` (observed on hardware
	// 2026-08-27 — an `upp` write, code 1, answered `:AAD3E7EC:0:success`
	// and the interval did change). So a reply from the right unit whose
	// result is a status word (`success` / `fail…`) counts as the
	// acknowledgement even when the code slot differs; the code match is
	// kept as the primary path for replies that do echo it.
	const matchesDevice =
		parsedResult != null && parsedResult.deviceId === deviceId;
	const matchesCode = matchesDevice && parsedResult.code === commandCode;
	const isStatusWord =
		parsedResult != null &&
		(parsedResult.result === 'success' ||
			parsedResult.result.startsWith('fail'));
	// A genuine device result carries a status word, not the value we sent.
	const parsed =
		matchesDevice &&
		(matchesCode || isStatusWord) &&
		parsedResult.result !== value &&
		!rawReadback;
	return {
		parsed,
		result: parsed ? parsedResult.result : null,
		rawReadback,
		rawEcho: clean,
	};
}

/**
 * Whether a string setting read back from a unit contains control characters.
 *
 * Units configured before the terminator correction (2026-08-26) can hold a
 * trailing CR inside `url` / `api_key` / `apn_name`, which renders as nothing
 * and silently breaks connection or authentication. Re-writing the field with
 * the corrected terminator clears it.
 */
export function hasControlCharacters(
	value: string | null | undefined,
): boolean {
	if (value == null) {
		return false;
	}
	// eslint-disable-next-line no-control-regex
	return /[\u0000-\u001f]/.test(value);
}

/**
 * Remove control characters from a value read back from a unit.
 *
 * Used to seed edit forms so the operator sees (and edits) the clean value,
 * while the raw stored value stays available for change detection — a field
 * whose stored form is corrupted must be re-written even when the visible
 * text is identical.
 */
export function stripControlCharacters(value: string): string {
	// eslint-disable-next-line no-control-regex
	return value.replace(/[\u0000-\u001f]/g, '');
}
